// ─────────────────────────────────────────────────────────────────────────────
// Upcoming, CD's right-rail next-events card. Populated from the schedule blob
// (lib fetchUpcomingShows → /api/data/schedule). Each row: a date block (dow +
// day), the title + subtitle, and a kind pill (Show / Rehearsal). Empty: CD's
// "Nothing upcoming yet." note. The count tag shows the number of rows.
// ─────────────────────────────────────────────────────────────────────────────

import type { JSX } from 'react';
import type { UpcomingShow } from '../../lib';
import { dowShort, dayOfMonth } from './format';

export interface UpcomingProps {
  readonly shows: ReadonlyArray<UpcomingShow>;
}

/** CD has two kind pills: --show and --rehearsal. Map other kinds to --show. */
function kindClass(kind: string): string {
  return kind.toLowerCase() === 'rehearsal' ? 'up-kind--rehearsal' : 'up-kind--show';
}

export function Upcoming({ shows }: UpcomingProps): JSX.Element {
  return (
    <section className="card" id="upcoming-shows-card">
      <header className="card__head">
        <h3 className="card__title">Upcoming</h3>
        <span className="tag">{shows.length}</span>
      </header>
      <div className="card__body" style={{ paddingTop: 6 }}>
        <ul className="upcoming-list">
          {shows.length === 0 ? (
            // ── EMPTY (dashboard-empty.html) ──
            <li>
              <p className="empty-note">Nothing upcoming yet.</p>
            </li>
          ) : (
            shows.map((s) => (
              <li className="up-row" key={s.id}>
                <div className="up-date">
                  <div className="up-date__dow">{dowShort(s.date)}</div>
                  <div className="up-date__day">{dayOfMonth(s.date)}</div>
                </div>
                <div className="up-main">
                  <p className="up-main__title">{s.title}</p>
                  <p className="up-main__sub">{s.subtitle}</p>
                </div>
                <span className={'up-kind ' + kindClass(s.kind)}>{s.kind}</span>
              </li>
            ))
          )}
        </ul>
      </div>
    </section>
  );
}
